import { Link } from "react-router-dom";
import { ArrowRight, Truck, GraduationCap, Factory, ShoppingCart, Heart, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";

const industries = [
  {
    icon: Truck,
    name: "Transportation & Logistics",
    description: "Optimize your supply chain with cutting-edge technology solutions. From real-time tracking to route optimization, we help logistics companies operate more efficiently.",
    challenges: [
      "Complex supply chain visibility",
      "Rising operational costs",
      "Customer delivery expectations",
      "Regulatory compliance"
    ],
    solutions: [
      "Real-time shipment tracking",
      "Route optimization with AI",
      "Warehouse management systems",
      "Automated customs documentation"
    ],
    image: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=800"
  },
  {
    icon: GraduationCap,
    name: "Education",
    description: "Transform learning experiences with digital innovation. We help educational institutions modernize their technology infrastructure and deliver engaging digital content.",
    challenges: [
      "Digital learning adoption",
      "Student engagement",
      "Administrative efficiency",
      "Data security and privacy"
    ],
    solutions: [
      "Learning management systems",
      "Student information systems",
      "Virtual classroom platforms",
      "Analytics and reporting"
    ],
    image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800"
  },
  {
    icon: Factory,
    name: "Manufacturing",
    description: "Drive Industry 4.0 transformation with smart manufacturing solutions. We help manufacturers leverage IoT, AI, and automation to improve productivity and quality.",
    challenges: [
      "Production efficiency",
      "Quality control",
      "Supply chain disruptions",
      "Workforce productivity"
    ],
    solutions: [
      "IoT-enabled monitoring",
      "Predictive maintenance",
      "Quality management systems",
      "Production planning optimization"
    ],
    image: "https://images.unsplash.com/photo-1565043666747-69f6646db940?w=800"
  },
  {
    icon: ShoppingCart,
    name: "Retail & E-Commerce",
    description: "Create seamless omnichannel experiences that delight customers. We help retailers unify their online and in-store operations while optimizing inventory and pricing.",
    challenges: [
      "Omnichannel integration",
      "Inventory management",
      "Customer experience",
      "Competitive pricing"
    ],
    solutions: [
      "Unified commerce platforms",
      "Real-time inventory visibility",
      "Personalization engines",
      "Dynamic pricing optimization"
    ],
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800"
  },
  {
    icon: Heart,
    name: "Healthcare",
    description: "Improve patient outcomes with digital health solutions. We help healthcare organizations modernize their systems while maintaining compliance and security.",
    challenges: [
      "Interoperability",
      "Patient engagement",
      "Regulatory compliance",
      "Data security"
    ],
    solutions: [
      "EHR integration",
      "Patient portals",
      "Telehealth platforms",
      "Healthcare analytics"
    ],
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800"
  },
  {
    icon: Landmark,
    name: "Financial Services",
    description: "Accelerate digital banking and fintech innovation. We help financial institutions modernize their core systems while ensuring security and compliance.",
    challenges: [
      "Legacy system modernization",
      "Regulatory compliance",
      "Cybersecurity threats",
      "Customer expectations"
    ],
    solutions: [
      "Core banking modernization",
      "Payment processing",
      "Risk and compliance",
      "Digital banking platforms"
    ],
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800"
  }
];

export default function Industries() {
  return (
    <div data-testid="industries-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
              Industry Expertise
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-white font-['Outfit'] mb-6">
              Deep Knowledge Across Industries
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed font-['Plus_Jakarta_Sans']">
              We bring industry-specific expertise to every engagement, understanding the unique 
              challenges and opportunities in each sector we serve.
            </p>
          </div>
        </div>
      </section>

      {/* Industries List */}
      <section className="bg-white">
        {industries.map((industry, index) => (
          <div 
            key={industry.name}
            data-testid={`industry-${industry.name.toLowerCase().replace(/\s+/g, '-')}`}
            className={`border-b border-slate-200 ${index % 2 === 1 ? 'bg-slate-50' : ''}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-blue-100 flex items-center justify-center">
                      <industry.icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">
                      {industry.name}
                    </h2>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-8 font-['Plus_Jakarta_Sans']">
                    {industry.description}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-4">
                        Key Challenges
                      </h3>
                      <ul className="space-y-2">
                        {industry.challenges.map((challenge) => (
                          <li key={challenge} className="flex items-start gap-2 text-sm text-slate-600">
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2" />
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-4">
                        Our Solutions
                      </h3>
                      <ul className="space-y-2">
                        {industry.solutions.map((solution) => (
                          <li key={solution} className="flex items-start gap-2 text-sm text-slate-600">
                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2" />
                            {solution}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className={`aspect-[4/3] overflow-hidden border border-slate-200 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <img 
                    src={industry.image} 
                    alt={industry.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA Section */}
      <section className="bg-blue-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Don't See Your Industry?
          </h2>
          <p className="text-blue-100 mb-8 font-['Plus_Jakarta_Sans']">
            Our expertise extends beyond these sectors. Let's discuss how we can help your business.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="industries-cta-button"
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-none px-8"
            >
              Contact Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
